/**
 * H2 · ফেরতের শর্ত — `/policy/refund/:key` (audit finding 11)
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame H2 (360 × 680).
 *
 * Frame note: "A promise with unknown terms is a dispute waiting to happen. The two-hour
 * window is stated as a reason, not a rule, and the no-scales case is answered rather
 * than left to the support agent to improvise."
 *
 * ── THE WORDS ARRIVE FROM THE API (W2-10) ───────────────────────────────────────
 * Every sentence on this sheet — the drawn chrome included — comes from
 * `GET /v1/policy/refund/:key` (`./api.ts § fetchRefundTerms`), which reads the
 * `refund_policies` row the checkout quote links to. Until W2-10 they were a constant in
 * `data.ts` for ONE key, so every other key opened nothing, and its third condition
 * promised a refund into অপ্রাপ্য ক্যাশ, which the founders withdrew on 2026-09-18. That
 * condition now says how the money actually comes back — the same way it was paid, by hand
 * — and it is authored, not drawn: `docs/deviations/W2-10.md` records the departure from
 * the frame and `docs/copy-review/W2-10.md` lists the sentence for review. Each condition
 * carries `data-copy-status` so the difference is visible in the markup, not only here.
 *
 * H2's closing note is drawn under the one policy the design draws, and it says the same
 * terms appear in support's "কিছু পণ্য কম আছে". The API sends it only for a key a support
 * problem actually carries; for any other key it is null and the note box is not drawn,
 * because the sentence would be false.
 *
 * KNOWN DEFECT 1 (three instances on this frame): the condition ordinals `১`, `২`, `৩`
 * are drawn `font:700 15px/1.4 Manrope,sans-serif`. They are Bangla numerals and Manrope
 * has none, so the browser resolves them through the declared `sans-serif` fallback.
 * docs/01 says build it as drawn and report it, so the drawn stack and the drawn 1.4 are
 * transcribed verbatim — substituting Anek Bangla here widened the cell by 3px and pushed
 * the whole right-hand column of all three rows off the design. Each ordinal sits in a
 * `flex:none` cell beside a 15px/1.75 title, so 1.4 does not change the row height.
 * REPORTED: the drawn font cannot render the drawn glyphs; a designer should either set
 * these in Anek Bangla (and accept the 3px reflow) or draw Latin ordinals.
 *
 * DEVIATIONS:
 *  · § 04's `Sheet` is not used — H2 is `18px 16px 16px` with numbered conditions and a
 *    borderless action, none of which the component expresses.
 *  · `বুঝেছি` is drawn as a 48px full-width teal word with no border and no fill. § 02's
 *    tertiary Button is `#12626F` (link), not `#0E3A45`. Transcribed; reported.
 */
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useResource } from '../../data/resource.js';
import { SurfaceStates } from '../../states/boundary.js';
import { reportUndrawn } from '../../states/unmapped.js';
import { Frame, SAND_FRAME } from '../offering/chrome.js';
import { fetchRefundTerms, type RefundTermsResponse } from './api.js';

/**
 * THE SHEET IS A PURE FUNCTION OF THE PAYLOAD, the split `PolicyDocument.tsx` makes: this
 * renders what the route returned, and the container below does the loading. Before the
 * answer arrives it is the dimmed checkout behind an empty space — the frame, and no sheet
 * — because a sheet with blank terms would read as terms that say nothing.
 */
export function H2RefundView({
  data,
  onAck,
}: {
  data: RefundTermsResponse | null;
  onAck?: () => void;
}): JSX.Element {
  return (
    <Frame className={`${SAND_FRAME} overflow-hidden`}>
      <div className="p-[16px] opacity-[.32] shrink min-h-0 overflow-hidden" aria-hidden>
        <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">
          {data?.backdrop_title_bn ?? ''}
        </div>
        <div className="bg-white border border-border h-[52px] box-content mt-[13px]" />
      </div>
      <div className="flex-1" />

      {data === null ? null : (
        <div
          role="dialog"
          aria-label={data.title_bn}
          className="bg-white border-t border-border shadow-sheet px-[16px] pt-[18px] pb-[16px]"
        >
          <div className="flex justify-center mb-[15px]">
            <div className="w-[36px] h-[3px] bg-border" />
          </div>

          <div
            data-copy-status={data.title_copy_status}
            className="font-anek font-semibold text-[19px] leading-[1.75] text-ink"
          >
            {data.title_bn}
          </div>
          <div className="font-anek font-normal text-[14px] leading-[1.75] text-body mt-[7px]">
            {data.intro_bn}
          </div>

          <div className="mt-[16px]">
            {data.conditions.map((c, i) => (
              <div
                key={c.ordinal_bn}
                className={
                  'py-[12px] border-t border-border flex gap-[12px]' +
                  (i === data.conditions.length - 1 ? ' border-b' : '')
                }
              >
                {/* Bangla numeral in the drawn `700 15px/1.4 Manrope,sans-serif`. See the header. */}
                <div className="font-manrope font-bold text-[15px] leading-[1.4] text-amber-text flex-none">
                  {c.ordinal_bn}
                </div>
                <div className="flex-1">
                  <div className="font-anek font-semibold text-[15px] leading-[1.75] text-ink">
                    {c.title_bn}
                  </div>
                  <div
                    data-copy-status={c.copy_status}
                    className="font-anek font-normal text-[13.5px] leading-[1.75] text-body mt-[2px]"
                  >
                    {c.body_bn}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {data.note_bn === null ? null : (
            <div className="border border-border bg-sand px-[13px] py-[11px] mt-[14px]">
              <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">
                {data.note_bn}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onAck}
            className="w-full h-[48px] flex items-center justify-center font-anek font-semibold text-[15px] leading-[1.75] text-teal mt-[8px] bg-transparent border-0 rounded-none"
          >
            {data.ack_bn}
          </button>
        </div>
      )}
    </Frame>
  );
}

/** The container: reads the key, loads its terms once, and hands the payload to the view. */
export function H2Refund(): JSX.Element | null {
  const navigate = useNavigate();
  const { key } = useParams();
  const asked = key !== undefined && key !== '';
  const { data, error, unmapped, pending } = useResource<RefundTermsResponse | null>(
    (signal) => (asked ? fetchRefundTerms(key, signal) : Promise.resolve(null)),
    `policy-refund-${key ?? ''}`,
  );
  // A failure with no designed state is `unmapped`, reported by `useResource` as itself —
  // not a missing policy, so it is not reported a second time as one.
  const missing = !pending && error === null && unmapped === null && data === null;

  /*
   * A KEY WITH NO WRITTEN TERMS RENDERS NOTHING, AND SAYS SO WHERE IT COUNTS.
   *
   * The API answers 404 for a key no row defines and for a row whose terms are still
   * 0300's placeholder; `fetchRefundTerms` turns that into `null`. The honest answer is no
   * sheet — the alternative is another product's promise under this heading, which is the
   * bug `refundPolicyByKey` once had (it answered every key with grocery_short_weight).
   *
   * In an EFFECT and not in the render body: this render runs twice under StrictMode and
   * a fault logged twice is a count nobody trusts (`states/unmapped.ts`). The customer's
   * half of this gap — a drawn Bangla sentence for "these terms are not written yet" —
   * is asked for in `docs/bangla-needed/session-safety.md` and is not invented here.
   */
  useEffect(() => {
    if (!missing) return;
    reportUndrawn(
      // The frame id and route, not the drawn title: this directory holds no Bangla
      // outside comments (`__tests__/policy.test.tsx`), and the log is read by staff.
      'H2 · /policy/refund/:key',
      `no written refund terms for key '${key ?? ''}', so no sheet is shown.`,
    );
  }, [missing, key]);

  if (missing) return null;

  return (
    <SurfaceStates surface="transaction" error={error ?? undefined}>
      <H2RefundView data={data} onAck={() => navigate(-1)} />
    </SurfaceStates>
  );
}
